import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authHeaders, getToken, setToken, TOKEN_KEYS } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [client, setClient] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const role = e.detail?.role;
      if (role === "client") setClient(null);
      if (role === "vendor") setVendor(null);
      if (role === "admin") setAdmin(null);
      if (e.detail?.message) toast.error(e.detail.message);
    };
    window.addEventListener("kn-auth-expired", handler);
    // Keep tabs in sync: if a role's token is removed/changed in another tab,
    // reflect it here so we never show a logged-in UI with a missing token.
    const onStorage = (ev) => {
      if (ev.key === null) return; // storage cleared
      if (ev.key === TOKEN_KEYS.client && !ev.newValue) setClient(null);
      if (ev.key === TOKEN_KEYS.vendor && !ev.newValue) setVendor(null);
      if (ev.key === TOKEN_KEYS.admin && !ev.newValue) setAdmin(null);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("kn-auth-expired", handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const loadMe = useCallback(async () => {
    const tasks = [];
    if (getToken("client")) {
      tasks.push(
        api.get("/client/auth/me", { headers: authHeaders("client") })
          .then((r) => setClient(r.data.client))
          .catch(() => setToken("client", null))
      );
    }
    if (getToken("vendor")) {
      tasks.push(
        api.get("/auth/me", { headers: authHeaders("vendor") })
          .then((r) => setVendor(r.data.vendor))
          .catch(() => setToken("vendor", null))
      );
    }
    if (getToken("admin")) {
      tasks.push(
        api.get("/admin/auth/me", { headers: authHeaders("admin") })
          .then((r) => setAdmin(r.data.admin))
          .catch(() => setToken("admin", null))
      );
    }
    await Promise.all(tasks);
    setReady(true);
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Immediate lockout: re-check the session whenever the app regains focus / becomes
  // visible again. If an account was deleted/suspended by an admin, /auth/me returns 401
  // and the axios interceptor clears the token and logs the user out right away.
  // IMPORTANT: on success we DO NOT overwrite client/vendor/admin state — doing so would
  // clobber in-progress form edits (e.g. the store setup form re-seeds from `vendor`).
  // We only need the ping to surface a 401; the interceptor handles the logout.
  const revalidateSessions = useCallback(() => {
    if (getToken("client")) api.get("/client/auth/me", { headers: authHeaders("client") }).catch(() => {});
    if (getToken("vendor")) api.get("/auth/me", { headers: authHeaders("vendor") }).catch(() => {});
    if (getToken("admin")) api.get("/admin/auth/me", { headers: authHeaders("admin") }).catch(() => {});
  }, []);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") revalidateSessions(); };
    window.addEventListener("focus", revalidateSessions);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", revalidateSessions);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [revalidateSessions]);

  // client
  const clientLoginToken = (data) => {
    setToken("client", data.token);
    setClient(data.client);
  };
  const clientLogout = () => {
    setToken("client", null);
    setClient(null);
  };
  const refreshClient = async () => {
    if (!getToken("client")) return;
    try {
      const r = await api.get("/client/auth/me", { headers: authHeaders("client") });
      setClient(r.data.client);
    } catch (_) {}
  };

  // vendor
  const vendorLoginToken = (data) => {
    setToken("vendor", data.token);
    setVendor(data.vendor);
  };
  const vendorLogout = () => {
    setToken("vendor", null);
    setVendor(null);
  };
  const refreshVendor = async () => {
    if (!getToken("vendor")) return;
    try {
      const r = await api.get("/auth/me", { headers: authHeaders("vendor") });
      setVendor(r.data.vendor);
    } catch (_) {}
  };

  // admin
  const adminLoginToken = (data) => {
    setToken("admin", data.token);
    setAdmin(data.admin);
  };
  const adminLogout = () => {
    setToken("admin", null);
    setAdmin(null);
  };

  return (
    <AuthCtx.Provider
      value={{
        ready,
        client, clientLoginToken, clientLogout, refreshClient,
        vendor, vendorLoginToken, vendorLogout, refreshVendor,
        admin, adminLoginToken, adminLogout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
